import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { ButtonComponent, IconComponent } from '@core/user-interface-legacy';

import type { MainTab, ProductTab } from '../../services/product-updates-state.service';

@Component({
  selector: 'pu-mobile-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, IconComponent],
  templateUrl: './mobile-menu.component.html',
  styleUrls: ['./mobile-menu.component.less'],
  host: {
    '(document:keydown.escape)': 'onEscapeKey()',
  },
})
export class MobileMenuComponent {
  // Inputs
  public readonly isOpen = input.required<boolean>();
  public readonly activeMainTab = input.required<MainTab>();
  public readonly activeProductTab = input.required<ProductTab>();
  public readonly currentLocale = input.required<string>();
  public readonly availableLocales = input.required<string[]>();
  public readonly isDarkMode = input.required<boolean>();

  // Outputs
  public readonly closeMenu = output<void>();
  public readonly mainTabChange = output<MainTab>();
  public readonly productTabChange = output<ProductTab>();
  public readonly localeChange = output<string>();
  public readonly themeToggle = output<void>();

  public readonly mainNavItems: Array<{ id: MainTab; label: string }> = [
    { id: 'whats-new', label: "What's New" },
    { id: 'coming-soon', label: 'Coming Soon' },
    { id: 'changelog', label: 'Changelog' },
  ];

  public readonly productTabs: Array<{ id: ProductTab; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'hire', label: 'Hire' },
    { id: 'manage', label: 'Manage' },
    { id: 'grow', label: 'Grow' },
    { id: 'ai', label: 'AI' },
  ];

  public readonly currentLocaleLabel = computed(() => this.getLanguageLabel(this.currentLocale()));

  public onBackdropClick(): void {
    this.closeMenu.emit();
  }

  public onPanelClick(event: Event): void {
    event.stopPropagation();
  }

  public onEscapeKey(): void {
    if (this.isOpen()) {
      this.closeMenu.emit();
    }
  }

  public onMainTabClick(tab: MainTab): void {
    this.mainTabChange.emit(tab);
  }

  public onProductTabClick(tab: ProductTab): void {
    this.productTabChange.emit(tab);
  }

  public onLocaleChange(event: Event): void {
    const select = <HTMLSelectElement>event.target;
    this.localeChange.emit(select.value);
  }

  public onThemeToggle(): void {
    this.themeToggle.emit();
  }

  public getLanguageLabel(locale: string): string {
    const labels: Record<string, string> = {
      en: 'English',
      de: 'Deutsch',
      fr: 'Français',
      nl: 'Nederlands',
      fi: 'Suomi',
      sv: 'Svenska',
    };
    return labels[locale] ?? locale.toUpperCase();
  }
}
